import { GoogleGenAI } from "@google/genai";

/**
 * Service for interacting with the Gemini AI API via the @google/genai SDK.
 * Using process.env.GEMINI_API_KEY as the primary key source.
 */
let aiInstance: any = null;

const getAI = () => {
  if (aiInstance) return aiInstance;
  
  let key: string | undefined;
  
  try {
    // Literal access for Vite define replacement
    key = process.env.GEMINI_API_KEY;
    
    if (!key || key === "AI Studio Free Tier") {
      key = (import.meta as any).env.VITE_GEMINI_API_KEY || key;
    }
  } catch (e) {
    console.error("SDB SECURITY: Error accessing key infrastructure", e);
  }

  // We attempt initialization even if the key looks like a placeholder,
  // as some platform environments might handle it.
  try {
    const sanitizedKey = (key || "").trim().replace(/^["']|["']$/g, '');
    aiInstance = new GoogleGenAI({ apiKey: sanitizedKey });
    return aiInstance;
  } catch (err) {
    console.error("SDB SECURITY: Failed to initialize AI SDK.", err);
    return null;
  }
};

export const geminiService = {
  /**
   * Internal helper to call the Gemini API using @google/genai patterns.
   */
  async _callAI(prompt: string, model: string = "gemini-3-flash-preview"): Promise<string> {
    try {
      const ai = getAI();
      if (!ai) throw new Error("AI Infrastructure not initialized.");

      const response = await ai.models.generateContent({
        model,
        contents: prompt,
      });

      // Use the .text property directly as per skill guidelines
      const text = response.text;
      if (!text) {
        throw new Error("Empty response from AI model.");
      }
      return text;
    } catch (error: any) {
      console.error("Gemini Service Error:", error);
      
      const msg = error.message || String(error);
      
      // Handle key issues specifically
      if (msg.includes("apiKey") || msg.includes("API key") || msg.includes("not valid") || msg.includes("401")) {
        return "Vault clearance failed: Access key missing or invalid. Please verify GEMINI_API_KEY in the AI Studio Secrets panel.";
      }
      
      if (msg.includes("quota") || msg.includes("429")) {
        return "Throughput Limit: The Archive is currently at capacity. Please try again in 60 seconds.";
      }

      return `Structural Error: The secure line was interrupted (${msg.substring(0, 60)}...)`;
    }
  },

  /**
   * Generates a full Executive Briefing based on a given title.
   * This provides "real" high-level psychological and strategic content.
   */
  async generateBriefing(title: string, category: string): Promise<string> {
    const prompt = `
      You are a high-level psychological consultant for "She Deserves Better," a prestigious relationship portal for high-net-worth women.
      Write an exclusive "Executive Briefing" or "Video Essay Transcript" titled "${title}" in the category of "${category}".
      
      The tone should be: clinical, sophisticated, architectural, and prestigious. 
      Avoid generic advice. Focus on structural dynamics, psychological leverage, and emotional autonomy.
      Use terms like "Boundaries Architecture," "Relational ROI," "Character Audit," and "Divestment."
      
      Format the response in professional Markdown with:
      1. A strong, provocative introduction.
      2. 3-4 deep-dive sections with architectural subheadings (e.g., "The Infrastructure of Silence").
      3. A summary "Executive Protocol" with 3 actionable steps.
      4. A concluding thought on prestige and autonomy.
    `;

    return this._callAI(prompt);
  },

  /**
   * Generates a structural rubric or toolkit for the Resource Vault.
   */
  async generateResource(title: string, type: string): Promise<string> {
    const prompt = `
      You are a specialized architect of performance-based relationships. 
      Generate a ${type} titled "${title}" as a downloadable resource for a VIP Member.
      
      This should be a practical, clinical toolkit. 
      Structure it as a formal document with:
      - Objective: The structural goal of this document.
      - Methodology: How to apply this framework.
      - The Rubric/Blueprint: 5-8 specific criteria or steps with detailed explanations.
      - Safety Protocols: Risk markers to watch for.
      
      Maintain the prestigious, clinical aesthetic of "She Deserves Better."
    `;

    return this._callAI(prompt);
  },

  /**
   * Generates a comprehensive clinical synthesis report based on a user's audit history.
   */
  async generateDiagnosticReport(audits: any[]): Promise<string> {
    if (!audits || audits.length === 0) return "Insufficient data to generate a comprehensive clinical synthesis. Please complete diagnostic audits.";

    // Simplify the payload so we don't send massive data, just the key details
    const synthesisData = audits.map(a => ({
      title: a.title,
      diagnostic: a.diagnostic,
      summary: a.summary,
      date: new Date(a.timestamp).toLocaleDateString()
    }));

    const prompt = `
      You are a high-level psychological consultant for "She Deserves Better," a prestigious relationship portal for high-net-worth women.
      Analyze the following history of diagnostic audits taken by a VIP member:
      
      ${JSON.stringify(synthesisData, null, 2)}
      
      Based on the responses and chronological progression of these audits, generate a "Comprehensive Diagnostic Synthesis."
      This should act as a high-level clinical report of their current relational architecture, defense mechanisms, and operational blind spots.
      
      The tone must be: clinical, sophisticated, highly empathetic yet highly analytical. Do not use generic self-help jargon.
      
      Format the response in professional Markdown with:
      1. A short, high-level summary (The Executive Overview).
      2. Key Structural Themes (What patterns emerge across these audits?).
      3. Critical Vulnerabilities or Blind Spots.
      4. Strategic Rectification Protocol (What should they focus on structurally moving forward?).
    `;

    return this._callAI(prompt);
  },

  /**
   * Generates a curated list of high-end recommendations based on a user's context.
   */
  async generateCuration(context: string): Promise<string> {
    const prompt = `
      You are the Head Concierge at "She Deserves Better." 
      A member has requested a structural curation for the following context: "${context}".
      
      Generate a curated selection of 3-4 ultra-high-end products or services.
      For each item, include:
      - Name & Brand (Real luxury brands like The Row, Goyard, Patek Philippe, etc.)
      - Architectural Category (e.g., "Logistics Architecture", "Wardrobe Foundation", "Nervous System Preservation")
      - Clinical Rationale: Why this item is structurally necessary for her specific context.
      
      The tone must be clinical, prestigious, and advisory.
      Format in Markdown.
    `;

    return this._callAI(prompt);
  }
};

